import Link from 'next/link'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { TrackClick } from '../../../../tracking/educatorTracking'
import { SectionContainer } from '../../SectionContainer'

interface PricingStaticData {
  productPriceText?: string
  isProductPriceAvailable?: boolean
}

interface SubjectAdmissionsSectionProps {
  ctaHref: string
  ctaLabel: string
  isPrimaryLogIn?: boolean
}

const FeatureRow = ({ text }: { text: string }) => (
  <div className='flex w-full flex-col gap-2.5 py-1.5'>
    <div className='h-px w-full bg-white/30' />
    <p className='m-0 text-body-3 leading-tight text-white'>{text}</p>
  </div>
)

export const SubjectAdmissionsSection = ({
  ctaHref,
  ctaLabel,
  isPrimaryLogIn = false,
}: SubjectAdmissionsSectionProps) => {
  const { t } = useTranslation('@mc/educator')
  const { productPriceText, isProductPriceAvailable } =
    useStaticData<PricingStaticData>()
  const price =
    isProductPriceAvailable && productPriceText
      ? productPriceText
      : t('enrollment.admissions.executive.items.priceNoAmount')

  const features = [
    t('subjectLandingPage.subjectAdmissions.features.duration'),
    t('subjectLandingPage.subjectAdmissions.features.certificate'),
    t('subjectLandingPage.subjectAdmissions.features.format'),
    t('subjectLandingPage.subjectAdmissions.features.aiLabs'),
    t('subjectLandingPage.subjectAdmissions.features.aiLearning'),
  ]

  return (
    <section data-section='subject-admissions' className='w-full pt-6'>
      <SectionContainer>
        <div className='bg-ja-blue-dark py-8 md:py-3 px-5'>
          <div className='mx-auto w-full md:w-[345px] md:py-8'>
            <div className='flex flex-col gap-8'>
              <p className='m-0 text-body-2 font-bold uppercase tracking-[0.32px] leading-tight text-white'>
                {t('subjectLandingPage.subjectAdmissions.label')}
              </p>

              <div className='flex flex-col gap-8'>
                <p className='m-0 text-body-2 leading-tight text-white'>
                  {t('subjectLandingPage.subjectAdmissions.intro')}
                  <sup className='text-label-2 italic'>
                    {` ${t('subjectLandingPage.subjectAdmissions.introFootnote')}`}
                  </sup>
                </p>

                <div className='flex flex-col'>
                  <div className='flex w-full flex-col gap-2.5 py-1.5'>
                    <div className='h-px w-full bg-white/30' />
                    <p className='m-0 text-body-3 font-semibold leading-tight text-white'>
                      {price}
                      <sup className='text-label-3 italic'>
                        {` ${t('subjectLandingPage.subjectAdmissions.priceFootnote')}`}
                      </sup>
                    </p>
                  </div>

                  {features.map((text, i) => (
                    <FeatureRow key={i} text={text} />
                  ))}

                  <div className='h-px w-full bg-white/30' />
                </div>
              </div>
            </div>

            <TrackClick
              event='educatorSignClick'
              action={ctaLabel.toLowerCase()}
              location='enrollment block'
            >
              {isPrimaryLogIn ? (
                <Link
                  href={ctaHref}
                  className='mt-6 flex w-full items-center justify-center bg-ja-blue-300 p-5 text-body-1 font-semibold leading-6 text-ja-blue-dark no-underline'
                >
                  {ctaLabel}
                </Link>
              ) : (
                <TrackClick
                  event='educatorPurchaseClick'
                  cta_location='enrollment block'
                  cta={ctaLabel}
                >
                  <Link
                    href={ctaHref}
                    className='mt-6 flex w-full items-center justify-center bg-ja-blue-300 p-5 text-body-1 font-semibold leading-6 text-ja-blue-dark no-underline'
                  >
                    {ctaLabel}
                  </Link>
                </TrackClick>
              )}
            </TrackClick>

            <div className='mt-4 flex flex-col gap-1'>
              <p className='m-0 text-label-2 leading-tight text-white'>
                <sup className='text-[6px]'>
                  {t(
                    'subjectLandingPage.subjectAdmissions.disclaimer.footnote1Marker',
                  )}
                </sup>
                {t(
                  'subjectLandingPage.subjectAdmissions.disclaimer.footnote1Text',
                )}
              </p>
              <p className='m-0 text-label-2 leading-tight text-white'>
                <sup className='text-[6px]'>
                  {t(
                    'subjectLandingPage.subjectAdmissions.disclaimer.footnote2Marker',
                  )}
                </sup>
                {t(
                  'subjectLandingPage.subjectAdmissions.disclaimer.footnote2Text',
                )}
              </p>
            </div>
          </div>
        </div>
      </SectionContainer>
    </section>
  )
}
